// One colour per category, shared by every Segment's detail card.
//
// In the chart above, colour means SEGMENT: one Segment reads as one colour
// across its bars, its cumulative line, and its summary row. Inside a Segment's
// own detail card every bar already belongs to that one Segment, so colour is
// free to carry the other identity — WHICH CATEGORY a bar is — and it is the
// only thing that lets the eye match "efficiency-productivity" in one card
// against the same category in the card beside it.
//
// Colours are therefore assigned from a category list built across ALL the
// Segments on screen, never from each card's own bar order: a Segment that is
// missing a category must not shift the colours of the categories it does have.
//
// These are the design system's categorical colours (`colors.categorical` in
// UI artifacts/design-system.json, published as --color-categorical-1..8).
// They are kept as literal hex here for the same reason SEGMENT_COLORS are:
// the analysis charts style through inline React styles and recharts props,
// neither of which resolves a CSS custom property.
//
// The order is validated, not cosmetic — worst adjacent pair CVD ΔE 9.2,
// normal-vision ΔE 16.3 on a white panel. Blue sits fourth on purpose: slot 1
// of the Segment palette is blue, and a leading blue category bar would read as
// "Segment A". None of the semantic tokens (success green, warning yellow,
// error red) appear: a status colour must keep meaning status. Aqua, magenta
// and yellow fall below 3:1 against the white panel, which is allowed here only
// because every bar is labelled beside itself and repeated as a count/percent
// row directly underneath.
export const CATEGORY_COLORS = ['#1BAF7A', '#EB6834', '#4A3AA7', '#3D85E6', '#008300', '#E87BA4', '#EDA100', '#E34948'];

// Past the palette the honest answer is "no colour identity", not a recycled
// hue: two categories sharing a colour would claim to be the same category.
// Their labels still carry the identity.
export const UNPALETTED_CATEGORY_COLOR = '#5F6368';

/**
 * @param {string[]} categories ordered category list shared by every Segment
 * @returns {Map<string, string>|null} colour by category, or null when there is
 *   no shared list to colour against (callers then keep their single fill).
 */
export function categoryColorMap(categories = []) {
  if (!categories.length) return null;
  const map = new Map();
  categories.forEach((category) => {
    if (map.has(category)) return;
    map.set(category, CATEGORY_COLORS[map.size] || UNPALETTED_CATEGORY_COLOR);
  });
  return map;
}
